import React from 'react';
import { FormattedMessage } from 'react-intl';
import { addNotification } from 'reapop';

import {
  size,
  get,
  keyBy,
  groupBy,
  map,
  join,
  reverse,
} from 'lodash';

import { custom } from 'utils/Api';

import {
  REQUEST_TRANSPORT_GRANTS,
  REQUEST_TRANSPORT_GRANTS_SUCCESS,
  REQUEST_TRANSPORT_GRANTS_FAILURE,
  EMPLOYEE_TRANSPORT_GRANT_REGISTER,
  EMPLOYEE_TRANSPORT_GRANT_REGISTER_SUCCESS,
  EMPLOYEE_TRANSPORT_GRANT_REGISTER_FAILURE,
} from './constants';

export const requestTransportGrants = () => ({
  type: REQUEST_TRANSPORT_GRANTS,
});

export const requestTransportGrantsSuccess = transportationGrants => ({
  type: REQUEST_TRANSPORT_GRANTS_SUCCESS,
  transportationGrants,
});

export const requestTransportGrantsFailure = error => ({
  type: REQUEST_TRANSPORT_GRANTS_FAILURE,
  error,
});

export const fetch = () => async (dispatch, getState) => {
  dispatch(requestTransportGrants());

  try {
    const {
      loginReducer: {
        user: { DefaultEmployeeDetailID, EmployeeMasterID, token },
      },
    } = getState();
    if (!DefaultEmployeeDetailID) {
      throw new Error('The user is missing a DefaultEmployeeDetailID');
    }
    const now = new Date().toISOString();
    const orgConfig = {
      headers: { Authorization: `Bearer ${token}` },
      url: `/kjarni/api/v2/EmployeeDetailOrgs/${DefaultEmployeeDetailID}/ByEmployeeDetailID`,
      params: {
        $filter: `Begda lt ${now} and Endda gt ${now}`,
        $select: 'EmployeeStatus,EmployeeGroup',
      },
    };
    const orgRes = await custom(orgConfig);
    const org = get(orgRes, 'data.value[0]');

    if (!org) {
      throw new Error('No EmployeeDetailOrg');
    }

    const grantsConfig = {
      headers: { Authorization: `Bearer ${token}` },
      url: '/kjarni/api/v2/HrTransportationGrants',
      params: {
        $filter: `EmployeeStatus in ('${org.EmployeeStatus}', 'None') and EmployeeGroup in ('${org.EmployeeGroup}', 'None')`,
      },
    };
    const grantsRes = await custom(grantsConfig);
    const grants = get(grantsRes, 'data.value', []);

    if (!size(grants)) {
      // If there are no transportation grants we don't need to fetch more data.
      dispatch(requestTransportGrantsSuccess([]));
      return;
    }

    const emailTemplateIds = map(grants, 'XapEmailTemplateID');

    const emailTemplateConfig = {
      headers: { Authorization: `Bearer ${token}` },
      url: '/kjarni/api/v2/XapEmailTemplates',
      params: {
        $filter: `XapEmailTemplateID in (${join(emailTemplateIds, ',')})`,
      },
    };
    const emTransportConfig = {
      headers: { Authorization: `Bearer ${token}` },
      url: '/kjarni/api/v2/EmployeeMasterTransportations',
      params: {
        $filter: `Endda gt ${now} and EmployeeMasterID eq ${EmployeeMasterID}`,
      },
    };
    const [
      emTransportsRes,
      emailTemplateRes,
    ] = await Promise.all([
      custom(emTransportConfig),
      custom(emailTemplateConfig),
    ]);
    const emTransports = groupBy(reverse(get(emTransportsRes, 'data.value')), 'HrTransportationGrantID');
    const emailTemplates = keyBy(get(emailTemplateRes, 'data.value'), 'XapEmailTemplateID');
    const grantsCombine = map(grants, grant => ({
      ...grant,
      Previous: get(emTransports, grant.HrTransportationGrantID),
      EmailTemplate: get(emailTemplates, grant.XapEmailTemplateID),
    }));
    dispatch(requestTransportGrantsSuccess(grantsCombine));
  } catch (error) {
    dispatch(requestTransportGrantsFailure(error.error || error));
  }
};

export const requestEmployeeTransportGrantRegister = transportationGrantId => ({
  type: EMPLOYEE_TRANSPORT_GRANT_REGISTER,
  transportationGrantId,
});

export const requestEmployeeTransportGrantRegisterSuccess = (transportationGrantId, employeeMasterTransportations) => ({
  type: EMPLOYEE_TRANSPORT_GRANT_REGISTER_SUCCESS,
  transportationGrantId,
  employeeMasterTransportations,
});

export const requestEmployeeTransportGrantRegisterFailure = (error, transportationGrantId) => ({
  type: EMPLOYEE_TRANSPORT_GRANT_REGISTER_FAILURE,
  transportationGrantId,
  error,
});

export const register = transportGrant =>
  async (dispatch, getState) => {
    if (!transportGrant || !transportGrant.HrTransportationGrantID) {
      return dispatch(requestEmployeeTransportGrantRegisterFailure(new Error('Missing TransportationGrant data/ID')));
    }
    const { HrTransportationGrantID } = transportGrant;
    try {
      dispatch(requestEmployeeTransportGrantRegister(HrTransportationGrantID));
      const { loginReducer: { token, user: { EmployeeMasterID } } } = getState();
      const registerConfig = {
        headers: { Authorization: `Bearer ${token}` },
        url: '/api/EmployeeTransportationGrant/Register',
        method: 'POST',
        data: transportGrant,
      };
      await custom(registerConfig);

      const now = new Date().toISOString();
      const emTransportsConfig = {
        headers: { Authorization: `Bearer ${token}` },
        url: '/kjarni/api/v2/EmployeeMasterTransportations',
        params: {
          $filter: `Endda gt ${now} and EmployeeMasterID eq ${EmployeeMasterID} and HrTransportationGrantID eq ${HrTransportationGrantID}`,
        },
      };

      dispatch(addNotification({
        title: <FormattedMessage id="notification.transportRegister.title" />,
        message: <FormattedMessage id="notification.transportRegister.message" />,
        status: 'success',
        dismissible: true,
        dismissAfter: 6000,
      }));

      const emTransportsRes = await custom(emTransportsConfig);
      const emTransports = get(emTransportsRes, 'data.value');
      return dispatch(requestEmployeeTransportGrantRegisterSuccess(HrTransportationGrantID, emTransports));
    } catch (error) {
      return dispatch(requestEmployeeTransportGrantRegisterFailure(error, HrTransportationGrantID));
    }
  };



// WEBPACK FOOTER //
// ./src/sites/transportationGrants/actions.js